/**
 * Layer Manager Composable
 *
 * Centralized management for Mapbox layers using ZLayers pattern:
 * - Uses empty z-layers as stable reference points for ordering
 * - Position-based: each group gets a position (0 = top of map)
 * - Tracks all layer groups and their sublayers
 * - Handles layer ordering via z-layer references
 * - Handles visibility toggling for entire groups
 */

import { ref, type Ref } from 'vue'
import type { Map as MapboxMap } from 'mapbox-gl'
import { SUBLAYER_ORDER, getSublayerType } from '@/components/layers/types'

// Used for sorting unknown sublayer types - places them after all known types
const UNKNOWN_SUBLAYER_SORT_ORDER = SUBLAYER_ORDER.length

export interface UseLayerManagerReturn {
  // State
  layerGroups: Ref<Map<string, string[]>>
  groupPositions: Ref<Map<string, number>>

  // Registration
  registerLayer: (groupId: string, layerId: string) => void
  unregisterLayer: (groupId: string, layerId: string) => void
  registerGroup: (groupId: string, position: number) => void
  unregisterGroup: (groupId: string) => void

  // Ordering
  moveGroupToPosition: (groupId: string, newPosition: number) => void
  getGroupBeforeId: (groupId: string) => string | undefined
  getBeforeIdForPosition: (position: number) => string

  // Visibility
  setGroupVisibility: (groupId: string, visible: boolean) => void
  getGroupVisibility: (groupId: string) => boolean

  // Utilities
  getGroupLayers: (groupId: string) => string[]
  isGroupRegistered: (groupId: string) => boolean
  getGroupPosition: (groupId: string) => number | undefined
}

// Singleton state shared across all instances
const globalLayerGroups = ref<Map<string, string[]>>(new Map())
const globalGroupPositions = ref<Map<string, number>>(new Map())
const globalGroupVisibility = ref<Map<string, boolean>>(new Map())

/**
 * Layer Manager composable
 * @param map - Reference to the Mapbox map instance
 */
export function useLayerManager(map?: Ref<MapboxMap | null>): UseLayerManagerReturn {
  /**
   * Get the beforeId (z-layer) for a given position
   * Position 0 = top of map, uses z-0
   */
  function getBeforeIdForPosition(position: number): string {
    return `z-${position}`
  }

  /**
   * Register a layer within a group
   * Layers are tracked and sorted by sublayer order
   */
  function registerLayer(groupId: string, layerId: string): void {
    const layers = globalLayerGroups.value.get(groupId) || []
    if (!layers.includes(layerId)) {
      layers.push(layerId)
      // Sort layers by sublayer order (unknown types go to end)
      layers.sort((a, b) => {
        const aType = getSublayerType(a, groupId) || ''
        const bType = getSublayerType(b, groupId) || ''
        const aIndex = SUBLAYER_ORDER.indexOf(aType as any)
        const bIndex = SUBLAYER_ORDER.indexOf(bType as any)
        const aSortOrder = aIndex === -1 ? UNKNOWN_SUBLAYER_SORT_ORDER : aIndex
        const bSortOrder = bIndex === -1 ? UNKNOWN_SUBLAYER_SORT_ORDER : bIndex
        return aSortOrder - bSortOrder
      })
      globalLayerGroups.value.set(groupId, layers)
    }
  }

  /**
   * Unregister a layer from a group
   */
  function unregisterLayer(groupId: string, layerId: string): void {
    const layers = globalLayerGroups.value.get(groupId)
    if (layers) {
      const index = layers.indexOf(layerId)
      if (index !== -1) {
        layers.splice(index, 1)
        if (layers.length === 0) {
          globalLayerGroups.value.delete(groupId)
        } else {
          globalLayerGroups.value.set(groupId, layers)
        }
      }
    }
  }

  /**
   * Register a new group with a position
   * Position determines which z-layer it uses (0 = top)
   */
  function registerGroup(groupId: string, position: number): void {
    globalGroupPositions.value.set(groupId, position)
    globalGroupVisibility.value.set(groupId, true) // Default visible
  }

  /**
   * Unregister a group
   */
  function unregisterGroup(groupId: string): void {
    globalGroupPositions.value.delete(groupId)
    globalLayerGroups.value.delete(groupId)
    globalGroupVisibility.value.delete(groupId)
  }

  /**
   * Move an entire layer group to a new position
   * This moves all sublayers to the new z-layer
   */
  function moveGroupToPosition(groupId: string, newPosition: number): void {
    if (!map?.value) return

    const mapInstance = map.value
    const layers = globalLayerGroups.value.get(groupId)
    if (!layers || layers.length === 0) return

    // Get the z-layer for the new position
    const beforeId = getBeforeIdForPosition(newPosition)

    // Move all layers in this group to the new z-layer
    for (const layerId of layers) {
      if (mapInstance.getLayer(layerId)) {
        try {
          mapInstance.moveLayer(layerId, beforeId)
        } catch (e) {
          console.warn(`[LayerManager] Failed to move layer ${layerId}:`, e)
        }
      }
    }

    // Update position tracking
    globalGroupPositions.value.set(groupId, newPosition)
  }

  /**
   * Get the beforeId (z-layer) for a group based on its position
   */
  function getGroupBeforeId(groupId: string): string | undefined {
    const position = globalGroupPositions.value.get(groupId)
    if (position === undefined) return undefined
    return getBeforeIdForPosition(position)
  }

  /**
   * Get current position of a group
   */
  function getGroupPosition(groupId: string): number | undefined {
    return globalGroupPositions.value.get(groupId)
  }

  /**
   * Set visibility for an entire layer group
   */
  function setGroupVisibility(groupId: string, visible: boolean): void {
    if (!map?.value) return

    const mapInstance = map.value
    const layers = globalLayerGroups.value.get(groupId)

    if (layers) {
      layers.forEach(layerId => {
        if (mapInstance.getLayer(layerId)) {
          try {
            mapInstance.setLayoutProperty(
              layerId,
              'visibility',
              visible ? 'visible' : 'none'
            )
          } catch (e) {
            console.warn(`[LayerManager] Failed to set visibility for layer ${layerId}:`, e)
          }
        }
      })
    }

    globalGroupVisibility.value.set(groupId, visible)
  }

  /**
   * Get current visibility state of a group
   */
  function getGroupVisibility(groupId: string): boolean {
    return globalGroupVisibility.value.get(groupId) ?? true
  }

  /**
   * Get all layers in a group
   */
  function getGroupLayers(groupId: string): string[] {
    return globalLayerGroups.value.get(groupId) || []
  }

  /**
   * Check if a group is registered
   */
  function isGroupRegistered(groupId: string): boolean {
    return globalGroupPositions.value.has(groupId)
  }

  return {
    layerGroups: globalLayerGroups,
    groupPositions: globalGroupPositions,
    registerLayer,
    unregisterLayer,
    registerGroup,
    unregisterGroup,
    moveGroupToPosition,
    getGroupBeforeId,
    getBeforeIdForPosition,
    setGroupVisibility,
    getGroupVisibility,
    getGroupLayers,
    isGroupRegistered,
    getGroupPosition
  }
}
